/**
 * Theme Utilities
 *
 * Helper functions for theme management and persistence
 * Clean, professional white theme with USLAND brand colors
 */

import {
  HighContrastMode,
  HIGH_CONTRAST_STORAGE_KEY,
  DEFAULT_HIGH_CONTRAST,
  FontSize,
  FONT_SIZE_STORAGE_KEY,
  DEFAULT_FONT_SIZE,
  FONT_SIZE_SCALES,
  ReducedMotionMode,
  REDUCED_MOTION_STORAGE_KEY,
  DEFAULT_REDUCED_MOTION,
  ThemeMode,
  THEME_STORAGE_KEY,
  DEFAULT_THEME_MODE,
  ColorTheme,
  DEFAULT_THEME,
} from '@/types/theme';

/**
 * Color theme storage key
 */
const COLOR_THEME_STORAGE_KEY = 'usland-color-theme';

/**
 * Apply color theme class to document
 * USLAND theme - defaults to light mode
 */
export function applyColorTheme(_theme?: ColorTheme): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;

  // Remove any old theme classes
  html.classList.forEach((className) => {
    if (className.startsWith('theme-')) {
      html.classList.remove(className);
    }
  });

  // Apply USLAND theme (light mode by default)
  html.classList.add('theme-usland');
}

/**
 * Initialize theme on page load
 * Defaults to USLAND light theme
 */
export function initializeTheme(): ColorTheme {
  applyColorTheme('usland');
  return 'usland';
}

/**
 * Get stored color theme - defaults to usland
 */
export function getStoredColorTheme(): ColorTheme {
  if (typeof window === 'undefined') return DEFAULT_THEME;

  try {
    const stored = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
    if (stored === 'usland') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read color theme:', error);
  }

  return DEFAULT_THEME;
}

/**
 * Save color theme to localStorage
 */
export function setStoredColorTheme(theme: ColorTheme): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(COLOR_THEME_STORAGE_KEY, theme);
  } catch (error) {
    console.warn('Failed to save color theme:', error);
  }

  applyColorTheme(theme);
}

/**
 * Get the stored theme mode (light/dark/system)
 */
export function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_THEME_MODE;

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read theme mode:', error);
  }

  return DEFAULT_THEME_MODE;
}

/**
 * Save theme mode to localStorage
 */
export function setStoredThemeMode(_mode: ThemeMode): void {
  // Dark only - no mode switching
}

/* ============================================================
   HIGH CONTRAST MODE
   ============================================================ */

export function applyHighContrastMode(enabled: boolean): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  if (enabled) {
    html.classList.add('high-contrast');
  } else {
    html.classList.remove('high-contrast');
  }
}

export function getStoredHighContrastMode(): HighContrastMode {
  if (typeof window === 'undefined') return DEFAULT_HIGH_CONTRAST;

  try {
    const stored = localStorage.getItem(HIGH_CONTRAST_STORAGE_KEY);
    if (stored === 'on' || stored === 'off') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read high contrast mode:', error);
  }

  return DEFAULT_HIGH_CONTRAST;
}

export function setStoredHighContrastMode(mode: HighContrastMode): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(HIGH_CONTRAST_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Failed to save high contrast mode:', error);
  }
}

export function initializeHighContrastMode(): HighContrastMode {
  const storedMode = getStoredHighContrastMode();
  applyHighContrastMode(storedMode === 'on');
  return storedMode;
}

/* ============================================================
   FONT SIZE
   ============================================================ */

export function applyFontSize(size: FontSize): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  const scale = FONT_SIZE_SCALES[size];

  // Remove existing font size classes
  html.classList.forEach((className) => {
    if (className.startsWith('font-size-')) {
      html.classList.remove(className);
    }
  });

  // Add new font size class
  html.classList.add(`font-size-${size}`);

  // Apply as inline style for immediate effect
  html.style.fontSize = `${scale}%`;
}

export function getStoredFontSize(): FontSize {
  if (typeof window === 'undefined') return DEFAULT_FONT_SIZE;

  try {
    const stored = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    if (stored && ['small', 'medium', 'large', 'xlarge'].includes(stored)) {
      return stored as FontSize;
    }
  } catch (error) {
    console.warn('Failed to read font size:', error);
  }

  return DEFAULT_FONT_SIZE;
}

export function setStoredFontSize(size: FontSize): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, size);
  } catch (error) {
    console.warn('Failed to save font size:', error);
  }
}

export function initializeFontSize(): FontSize {
  const storedSize = getStoredFontSize();
  applyFontSize(storedSize);
  return storedSize;
}

/* ============================================================
   REDUCED MOTION
   ============================================================ */

export function applyReducedMotionMode(enabled: boolean): void {
  if (typeof document === 'undefined') return;

  const html = document.documentElement;
  if (enabled) {
    html.classList.add('reduced-motion');
  } else {
    html.classList.remove('reduced-motion');
  }
}

export function getStoredReducedMotionMode(): ReducedMotionMode {
  if (typeof window === 'undefined') return DEFAULT_REDUCED_MOTION;

  try {
    const stored = localStorage.getItem(REDUCED_MOTION_STORAGE_KEY);
    if (stored === 'on' || stored === 'off') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read reduced motion mode:', error);
  }

  return DEFAULT_REDUCED_MOTION;
}

export function setStoredReducedMotionMode(mode: ReducedMotionMode): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(REDUCED_MOTION_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Failed to save reduced motion mode:', error);
  }
}

export function initializeReducedMotionMode(): ReducedMotionMode {
  const storedMode = getStoredReducedMotionMode();
  applyReducedMotionMode(storedMode === 'on');
  return storedMode;
}

/* ============================================================
   LEGACY COMPATIBILITY
   ============================================================ */

// For components that still expect getThemeConfig
export function getThemeConfig(_themeId?: string): null {
  return null;
}
